import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import DatePicker, { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import Icon from '../components/Icon';
import './EvenementDetail.css';

registerLocale('fr', fr);

// ─── Modal Message Clients ─────────────────────

function ModalMessageClients({ evenementId, nbConfirmees, slots, onClose }) {
  const [destinataires, setDestinataires] = useState('confirmes');
  const [sujet, setSujet]                 = useState('');
  const [message, setMessage]             = useState('');
  const [chargement, setChargement]       = useState(false);
  const [resultat, setResultat]           = useState(null);
  const [erreur, setErreur]               = useState('');

  async function envoyer() {
    if (!sujet.trim() || !message.trim()) {
      setErreur('Sujet et message requis.');
      return;
    }
    setChargement(true);
    setErreur('');
    try {
      const data = await api.post(`/evenements/${evenementId}/message-clients`, {
        destinataires,
        sujet,
        message,
      });
      setResultat(data);
    } catch (err) {
      setErreur(err?.response?.data?.error || 'Erreur lors de l\'envoi.');
    } finally {
      setChargement(false);
    }
  }

  const nbDest = destinataires === 'tous'
    ? 'Tous'
    : destinataires === 'confirmes'
    ? `Confirmés (${nbConfirmees || '?'})`
    : destinataires;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-contenu" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-titre">Message aux clients</h3>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>

        {resultat ? (
          <div style={{ padding: '20px 24px' }}>
            <div className="success" style={{ fontSize: 16, fontWeight: 600 }}>
              ✅ {resultat.envoyes} message{resultat.envoyes !== 1 ? 's' : ''} envoyé{resultat.envoyes !== 1 ? 's' : ''} / {resultat.total} destinataires
            </div>
            {resultat.erreurs?.length > 0 && (
              <div className="danger" style={{ marginTop: 8, fontSize: 13 }}>
                Erreurs : {resultat.erreurs.join(', ')}
              </div>
            )}
            <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={onClose}>Fermer</button>
          </div>
        ) : (
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="form-label">Destinataires</label>
              <select
                className="select-input"
                value={destinataires}
                onChange={e => setDestinataires(e.target.value)}
              >
                <option value="confirmes">Confirmés seulement ({nbConfirmees || '?'})</option>
                <option value="tous">Tous (hors annulés)</option>
                {(slots || []).map(s => (
                  <option key={s.slot} value={s.slot}>
                    Slot {s.slot} ({s.nb_commandes} commandes)
                  </option>
                ))}
              </select>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Sélection : {nbDest}
              </div>
            </div>
            <div>
              <label className="form-label">Sujet</label>
              <input
                className="input-text"
                placeholder="Ex : Important — infos de pickup"
                value={sujet}
                onChange={e => setSujet(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Message</label>
              <textarea
                className="textarea-input"
                rows={5}
                placeholder="Votre message..."
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
            {erreur && <div className="danger" style={{ fontSize: 13 }}>{erreur}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
              <button
                className="btn btn-primary"
                onClick={envoyer}
                disabled={chargement}
              >
                {chargement ? 'Envoi…' : `Envoyer →`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Export PDF feuille de service ────────────

async function exporterPDF(evenementId) {
  try {
    const data = await api.get(`/evenements/${evenementId}/export-pdf`);
    const ev   = data.evenement;

    // Construire le HTML de la feuille de service
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 24px; color: #1a1a1a;">
        <div style="text-align: center; margin-bottom: 24px; border-bottom: 2px solid #c8941a; padding-bottom: 16px;">
          <h1 style="font-size: 22px; color: #c8941a; margin: 0;">FEUILLE DE SERVICE — ${ev.nom}</h1>
          <p style="margin: 6px 0; color: #555;">${ev.date_event ? new Date(ev.date_event + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
          ${ev.lieu ? `<p style="margin: 4px 0; color: #555;">📍 ${ev.lieu}${ev.ville ? `, ${ev.ville}` : ''}</p>` : ''}
        </div>

        <h2 style="font-size: 16px; color: #c8941a; margin-bottom: 12px;">PRODUCTION TOTALE</h2>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th style="text-align: left; padding: 8px 12px; border: 1px solid #ddd;">Produit</th>
              <th style="text-align: right; padding: 8px 12px; border: 1px solid #ddd;">Quantité</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(data.production || {}).map(([label, qte]) =>
              `<tr><td style="padding: 8px 12px; border: 1px solid #ddd;">${label}</td><td style="text-align: right; padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${qte}</td></tr>`
            ).join('')}
          </tbody>
        </table>

        <h2 style="font-size: 16px; color: #c8941a; margin-bottom: 12px;">PAR CRÉNEAU</h2>
        ${(data.par_slot || []).map(s => `
          <div style="margin-bottom: 8px; padding: 8px 12px; background: #f9f9f9; border-left: 3px solid #c8941a;">
            <strong>${s.slot}</strong> — ${s.nb_commandes} commandes · ${s.nb_personnes} personnes
          </div>
        `).join('')}

        <h2 style="font-size: 16px; color: #c8941a; margin: 24px 0 12px;">LISTE DES COMMANDES</h2>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background: #1a1a1a; color: #fff;">
              <th style="padding: 8px; text-align: left;">N° Conf.</th>
              <th style="padding: 8px; text-align: left;">Créneau</th>
              <th style="padding: 8px; text-align: left;">Client</th>
              <th style="padding: 8px; text-align: left;">Plateaux</th>
              <th style="padding: 8px; text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${(data.par_slot || []).flatMap(s => s.commandes || []).map((cmd, i) => `
              <tr style="background: ${i % 2 === 0 ? '#fff' : '#f9f9f9'};">
                <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-family: monospace;">${cmd.numero_conf}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${cmd.slot_pickup}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${cmd.client_prenom} ${cmd.client_nom}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #eee;">${(cmd.items || []).map(it => `${it.quantite || 1}×${it.label || it.taille}`).join(', ')}</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(cmd.montant_total || 0).toFixed(2)} $</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div style="margin-top: 24px; border-top: 1px solid #ddd; padding-top: 12px; text-align: right; color: #555; font-size: 13px;">
          ${data.totaux?.nb_commandes_confirmees || 0} commandes ·
          ${data.totaux?.personnes_confirmees || 0} personnes ·
          ${parseFloat(data.totaux?.revenus_confirmes || 0).toFixed(2)} $
        </div>
      </div>
    `;

    // Utiliser html2pdf.js
    const html2pdf = (await import('html2pdf.js')).default;
    const element  = document.createElement('div');
    element.innerHTML = html;
    document.body.appendChild(element);

    await html2pdf()
      .set({
        margin:      10,
        filename:    `Escamel_FeuilleService_${ev.nom.replace(/\s+/g, '_')}.pdf`,
        image:       { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF:       { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(element)
      .save();

    document.body.removeChild(element);
  } catch (err) {
    console.error('[Export PDF]', err);
    alert('Erreur lors de la génération du PDF : ' + (err?.message || err));
  }
}

// ─── Config ───────────────────────────────────

const PUBLIC_FORM_URL = process.env.REACT_APP_PUBLIC_FORM_URL || 'http://localhost:3002';

// ─── Helpers ──────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmt$(v) {
  if (v == null) return '0,00 $';
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${parseFloat(v).toFixed(1)}%`;
}

const ALLERGIES_OPTIONS = [
  'Gluten',
  'Produits laitiers',
  'Arachides',
  'Noix',
  'Soya',
  'Oeufs',
  'Moutarde',
  'Sésame',
  'Fruits de mer',
];

function parseNombre(valeur) {
  const n = parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
}

function formatListePlateaux(items = []) {
  return items.map((item) => `${item.quantite || item.qte || 1}× ${item.label || item.taille}`).join(', ');
}

function formatResumeBoissons(boissons = []) {
  const comptage = boissons.reduce((acc, item) => {
    if (!item?.boisson) return acc;
    acc[item.boisson] = (acc[item.boisson] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(comptage).map(([nom, nb]) => `${nom}×${nb}`).join(', ');
}

function formatResumeExtras(extras = []) {
  return extras.map((extra) => `${extra.emoji || '🍖'}×${extra.qte || extra.quantite || 0}`).join(', ');
}

// ─── Badges ───────────────────────────────────

const STATUTS_EV = {
  brouillon: { label: 'Brouillon', cls: 'badge-muted' },
  publie:    { label: 'Publié',    cls: 'badge-success' },
  complet:   { label: 'Complet',   cls: 'badge-gold' },
  termine:   { label: 'Terminé',   cls: 'badge-info' },
  annule:    { label: 'Annulé',    cls: 'badge-danger' },
};

const STATUTS_CMD = {
  confirmee: { label: 'Confirmée', cls: 'badge-success' },
  prete:     { label: 'Prête',     cls: 'badge-gold' },
  remise:    { label: 'Remise',    cls: 'badge-info' },
  annulee:   { label: 'Annulée',   cls: 'badge-danger' },
  en_attente:{ label: 'En attente',cls: 'badge-muted' },
};

function StatutBadge({ statut, map }) {
  const s = (map || STATUTS_EV)[statut] || { label: statut, cls: 'badge-muted' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

// ─── Onglet 1 : Aperçu ────────────────────────

function OngletApercu({ ev, slots, plateaux, onAction, chargement, msgAction }) {
  const pctCapacite = ev.capacite_totale > 0
    ? Math.min(100, Math.round((ev.nb_personnes || 0) / ev.capacite_totale * 100))
    : 0;

  function couleurSlot(nb, max) {
    const pct = max > 0 ? nb / max : 0;
    if (pct >= 1)   return 'var(--muted)';
    if (pct >= 0.8) return 'var(--danger)';
    if (pct >= 0.5) return 'var(--warning)';
    return 'var(--success)';
  }

  return (
    <div className="onglet-contenu">
      {/* Infos événement */}
      <div className="apercu-grid">
        <div className="card apercu-card">
          <h3 className="apercu-section-titre">Informations</h3>
          <div className="apercu-infos">
            <div className="apercu-ligne">
              <span className="muted">Lieu</span>
              <span>{ev.lieu || '—'}</span>
            </div>
            {ev.adresse && (
              <div className="apercu-ligne">
                <span className="muted">Adresse</span>
                <span>{ev.adresse}{ev.ville ? `, ${ev.ville}` : ''}</span>
              </div>
            )}
            <div className="apercu-ligne">
              <span className="muted">Date</span>
              <span>{fmtDate(ev.date_evenement)}</span>
            </div>
            {ev.date_limite_precommande && (
              <div className="apercu-ligne">
                <span className="muted">Limite précommande</span>
                <span>{fmtDate(ev.date_limite_precommande)}</span>
              </div>
            )}
            <div className="apercu-ligne">
              <span className="muted">Statut</span>
              <StatutBadge statut={ev.statut} />
            </div>
          </div>

          {/* Actions */}
          <div className="apercu-actions">
            {ev.statut === 'brouillon' && (
              <button
                className="btn btn-primary"
                onClick={() => onAction('publie')}
                disabled={chargement}
              >
                Publier
              </button>
            )}
            {ev.statut === 'publie' && (
              <button
                className="btn btn-primary"
                onClick={() => onAction('termine')}
                disabled={chargement}
              >
                Marquer terminé
              </button>
            )}
            <button
              className="btn btn-ghost"
              onClick={() => onAction('modifier')}
            >
              Modifier événement
            </button>
            {(ev.statut === 'publie' || ev.statut === 'complet') && ev.slug ? (
              <a
                className="btn btn-ghost apercu-lien-public"
                href={`${PUBLIC_FORM_URL}/precommande/${ev.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Voir la page client
              </a>
            ) : (
              <button
                className="btn btn-ghost apercu-lien-public"
                disabled
                title="Publiez l'événement d'abord"
              >
                Voir la page client
              </button>
            )}
          </div>
          {msgAction && (
            <div className={`apercu-msg-action ${msgAction.startsWith('Erreur') ? 'msg-erreur' : 'msg-success'}`}>
              {msgAction}
            </div>
          )}
        </div>

        {/* Jauge capacité */}
        <div className="card apercu-card">
          <h3 className="apercu-section-titre">Capacité</h3>
          <div className="capacite-chiffres">
            <span className="capacite-nb">{ev.nb_personnes || 0}</span>
            <span className="muted"> / {ev.capacite_totale} personnes</span>
          </div>
          <div className="capacite-barre-grande">
            <div
              className="capacite-barre-fill"
              style={{
                width: `${pctCapacite}%`,
                background: pctCapacite >= 90 ? 'var(--danger)'
                  : pctCapacite >= 70 ? 'var(--warning)'
                  : 'var(--gold-500)',
              }}
            />
          </div>
          <div className="capacite-pct muted">{pctCapacite}% rempli</div>
        </div>
      </div>

      {/* Slots en temps réel */}
      {slots && slots.length > 0 && (
        <div className="card" style={{ padding: '20px 24px' }}>
          <h3 className="apercu-section-titre">Fenêtres horaires</h3>
          <div className="slots-grille">
            {slots.map(slot => {
              const max = ev.capacite_slot || 40;
              const couleur = couleurSlot(slot.nb_personnes, max);
              const plein = slot.nb_personnes >= max;
              return (
                <div
                  key={slot.heure}
                  className={`slot-card ${plein ? 'slot-plein' : ''}`}
                  style={{ borderColor: couleur }}
                >
                  <div className="slot-heure">{slot.heure}</div>
                  <div className="slot-compteur" style={{ color: couleur }}>
                    {slot.nb_personnes} / {max}
                  </div>
                  <div className="slot-barre">
                    <div
                      className="slot-barre-fill"
                      style={{
                        width: `${Math.min(100, (slot.nb_personnes / max) * 100)}%`,
                        background: couleur,
                      }}
                    />
                  </div>
                  {plein && <div className="slot-label-plein muted">Complet</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plateaux */}
      {plateaux && plateaux.length > 0 && (
        <div className="card" style={{ padding: '20px 24px' }}>
          <h3 className="apercu-section-titre">Plateaux & prix</h3>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Taille</th>
                <th>Libellé</th>
                <th>Prix</th>
                <th>Actif</th>
              </tr>
            </thead>
            <tbody>
              {plateaux.map(p => (
                <tr key={p.id}>
                  <td><span className="badge badge-muted">{p.taille}</span></td>
                  <td>{p.label || '—'}</td>
                  <td className="gold">{fmt$(p.prix)}</td>
                  <td>
                    <span className={`badge ${p.actif ? 'badge-success' : 'badge-muted'}`}>
                      {p.actif ? 'Oui' : 'Non'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Onglet 2 : Commandes ─────────────────────

function OngletCommandes({ evenementId }) {
  const [commandes, setCommandes] = useState([]);
  const [catalogueExtras, setCatalogueExtras] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreSlot, setFiltreSlot] = useState('tous');
  const [expandedId, setExpandedId] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [dataCommandes, dataExtras] = await Promise.all([
        api.get(`/evenements/${evenementId}/commandes`),
        api.get(`/evenements/${evenementId}/extras`),
      ]);
      setCommandes(Array.isArray(dataCommandes) ? dataCommandes : []);
      setCatalogueExtras(Array.isArray(dataExtras) ? dataExtras : []);
    } catch {
      setCommandes([]);
      setCatalogueExtras([]);
    } finally {
      setChargement(false);
    }
  }, [evenementId]);

  useEffect(() => { charger(); }, [charger]);

  const boissonsDisponibles = catalogueExtras.filter((extra) => extra.categorie === 'boisson_incluse');
  const extrasDisponibles = catalogueExtras.filter((extra) => extra.categorie !== 'boisson_incluse' && extra.actif !== 0);
  const slotsUniques = ['tous', ...new Set(commandes.map((cmd) => cmd.slot_pickup).filter(Boolean))];

  function initialiserEdition(cmd) {
    const nbPersonnes = parseInt(cmd.nb_personnes_total, 10) || 0;
    const boissons = Array.from({ length: nbPersonnes }, (_, index) => {
      const existante = (cmd.boissons_choisies || []).find((item) => (parseInt(item.personne, 10) || 0) === index + 1);
      return {
        personne: index + 1,
        boisson: existante?.boisson || '',
      };
    });

    setEdits((prev) => ({
      ...prev,
      [cmd.id]: {
        boissons_choisies: boissons,
        extras: extrasDisponibles.map((extra) => {
          const courant = (cmd.extras || []).find((item) => String(item.extra_id || '') === String(extra.id) || item.nom === extra.nom);
          return {
            extra_id: extra.id,
            nom: extra.nom,
            prix_unitaire: extra.prix,
            emoji: extra.emoji,
            qte: courant ? parseInt(courant.qte || courant.quantite, 10) || 0 : 0,
          };
        }),
        allergies: Array.isArray(cmd.allergies) ? [...cmd.allergies] : [],
        notes_allergies: cmd.notes_allergies || '',
        notes_internes: cmd.notes_internes || '',
      },
    }));
  }

  function toggleExpanded(cmd) {
    if (expandedId === cmd.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(cmd.id);
    initialiserEdition(cmd);
  }

  function majEdit(cmdId, champ, valeur) {
    setEdits((prev) => ({
      ...prev,
      [cmdId]: {
        ...(prev[cmdId] || {}),
        [champ]: valeur,
      },
    }));
  }

  async function changerStatut(cmdId, statut) {
    try {
      await api.put(`/precommandes/${cmdId}/statut`, { statut });
      await charger();
    } catch {
      setMessage('Erreur lors de la mise à jour du statut.');
    }
  }

  async function sauvegarderDetails(cmdId) {
    const edit = edits[cmdId];
    if (!edit) return;
    setSavingId(cmdId);
    setMessage('');
    try {
      const body = {
        boissons_choisies: (edit.boissons_choisies || []).filter((item) => item.boisson),
        extras: (edit.extras || []).filter((item) => parseInt(item.qte, 10) > 0),
        allergies: edit.allergies || [],
        notes_allergies: edit.notes_allergies || '',
        notes_internes: edit.notes_internes || '',
      };
      await api.put(`/precommandes/${cmdId}/details`, body);
      setMessage('Modifications sauvegardées.');
      await charger();
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingId(null);
    }
  }

  const commandesFiltrees = commandes.filter((cmd) => {
    if (filtreStatut !== 'tous' && cmd.statut !== filtreStatut) return false;
    if (filtreSlot !== 'tous' && cmd.slot_pickup !== filtreSlot) return false;
    return true;
  });

  return (
    <div className="onglet-contenu">
      <div className="commandes-filtres">
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} className="filtre-select">
          <option value="tous">Tous les statuts</option>
          <option value="confirmee">Confirmée</option>
          <option value="prete">Prête</option>
          <option value="remise">Remise</option>
          <option value="annulee">Annulée</option>
          <option value="en_attente">En attente</option>
        </select>
        <select value={filtreSlot} onChange={(e) => setFiltreSlot(e.target.value)} className="filtre-select">
          {slotsUniques.map((slot) => (
            <option key={slot} value={slot}>{slot === 'tous' ? 'Tous les créneaux' : slot}</option>
          ))}
        </select>
      </div>

      {message && <div className="msg-success">{message}</div>}

      {chargement ? (
        <div className="muted" style={{ padding: 24 }}>Chargement…</div>
      ) : commandesFiltrees.length === 0 ? (
        <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Aucune commande trouvée.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="esc-table">
            <thead>
              <tr>
                <th># Conf</th>
                <th>Nom</th>
                <th>Plateaux</th>
                <th>Slot</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Extras</th>
                <th>Allergies</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commandesFiltrees.map((cmd) => {
                const edit = edits[cmd.id] || {};
                const extraActifs = (cmd.extras || []).filter((extra) => parseInt(extra.qte || extra.quantite, 10) > 0);
                return (
                  <React.Fragment key={cmd.id}>
                    <tr className={`cmd-row ${expandedId === cmd.id ? 'cmd-row-expanded' : ''}`} onClick={() => toggleExpanded(cmd)}>
                      <td><span className="cmd-num">{cmd.numero_conf}</span></td>
                      <td>
                        <div>{cmd.client_prenom} {cmd.client_nom}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{cmd.client_email}</div>
                      </td>
                      <td>{formatListePlateaux(cmd.items) || '—'}</td>
                      <td>{cmd.slot_pickup || '—'}</td>
                      <td className="gold">{fmt$(cmd.montant_total)}</td>
                      <td><StatutBadge statut={cmd.statut} map={STATUTS_CMD} /></td>
                      <td>
                        {extraActifs.length > 0 ? (
                          <span className="badge badge-gold">🍖 Extras ({extraActifs.length})</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {(cmd.allergies || []).length > 0 ? (
                          <span className="badge badge-danger">Allergies</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cmd-actions">
                          {cmd.statut === 'confirmee' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => changerStatut(cmd.id, 'prete')}>Marquer prête</button>
                          )}
                          {cmd.statut === 'prete' && (
                            <button className="btn btn-primary btn-sm" onClick={() => changerStatut(cmd.id, 'remise')}>Marquer remise</button>
                          )}
                          {cmd.statut !== 'annulee' && cmd.statut !== 'remise' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => changerStatut(cmd.id, 'annulee')}>Annuler</button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedId === cmd.id && (
                      <tr className="cmd-detail-row">
                        <td colSpan={9}>
                          <div className="cmd-detail cmd-detail-stack">
                            <div className="cmd-detail-topline">
                              <div>
                                <div className="cmd-num">{cmd.numero_conf}</div>
                                <div>{cmd.client_prenom} {cmd.client_nom}</div>
                              </div>
                              <div className="cmd-detail-inline">
                                <span><strong>Total :</strong> {fmt$(cmd.montant_total)}</span>
                                <span><strong>Créneau :</strong> {cmd.slot_pickup}</span>
                                <StatutBadge statut={cmd.statut} map={STATUTS_CMD} />
                              </div>
                            </div>

                            <div className="cmd-detail-grid">
                              <div className="cmd-detail-section">
                                <strong>Plateaux</strong>
                                <ul className="cmd-items-liste">
                                  {(cmd.items || []).map((item, i) => (
                                    <li key={i}>{item.quantite || item.qte || 1}× {item.label || item.taille} <span className="gold">{fmt$((item.prix_unitaire || 0) * (item.quantite || item.qte || 1))}</span></li>
                                  ))}
                                </ul>
                              </div>

                              <div className="cmd-detail-section">
                                <strong>Boissons choisies ({cmd.nb_personnes_total || (edit.boissons_choisies || []).length} personnes)</strong>
                                <div className="cmd-edit-list">
                                  {(edit.boissons_choisies || []).map((boisson, index) => (
                                    <div key={index} className="cmd-edit-row">
                                      <span>Personne {boisson.personne || index + 1}</span>
                                      <select
                                        className="filtre-select"
                                        value={boisson.boisson || ''}
                                        onChange={(e) => {
                                          const prochaines = [...(edit.boissons_choisies || [])];
                                          prochaines[index] = { ...prochaines[index], boisson: e.target.value };
                                          majEdit(cmd.id, 'boissons_choisies', prochaines);
                                        }}
                                      >
                                        <option value="">Choisir</option>
                                        {boissonsDisponibles.map((option) => (
                                          <option key={option.id} value={option.nom}>{option.nom}</option>
                                        ))}
                                      </select>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="cmd-detail-section">
                                <strong>Extras commandés</strong>
                                <div className="cmd-edit-list">
                                  {(edit.extras || []).map((extra) => (
                                    <div key={extra.extra_id} className="cmd-edit-row">
                                      <span>{extra.emoji || '🍖'} {extra.nom}</span>
                                      <input
                                        type="number"
                                        min="0"
                                        className="param-input"
                                        value={extra.qte}
                                        onChange={(e) => {
                                          const prochaines = (edit.extras || []).map((item) => item.extra_id === extra.extra_id ? { ...item, qte: e.target.value } : item);
                                          majEdit(cmd.id, 'extras', prochaines);
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="cmd-detail-section">
                                <strong style={{ color: 'var(--danger)' }}>Allergies déclarées</strong>
                                <div className="cmd-allergies">
                                  {ALLERGIES_OPTIONS.map((allergie) => {
                                    const active = (edit.allergies || []).includes(allergie);
                                    return (
                                      <button
                                        key={allergie}
                                        className={`btn-allergie ${active ? 'active' : ''}`}
                                        onClick={() => {
                                          const prochaines = active
                                            ? (edit.allergies || []).filter((item) => item !== allergie)
                                            : [...(edit.allergies || []), allergie];
                                          majEdit(cmd.id, 'allergies', prochaines);
                                        }}
                                      >
                                        {allergie}
                                      </button>
                                    );
                                  })}
                                </div>
                                <textarea
                                  className="textarea-input"
                                  rows={2}
                                  placeholder="Note allergies"
                                  value={edit.notes_allergies || ''}
                                  onChange={(e) => majEdit(cmd.id, 'notes_allergies', e.target.value)}
                                />
                              </div>

                              {cmd.notes_client && (
                                <div className="cmd-detail-section">
                                  <strong>Notes client</strong>
                                  <p className="muted">"{cmd.notes_client}"</p>
                                </div>
                              )}

                              <div className="cmd-detail-section">
                                <strong>Notes internes</strong>
                                <textarea
                                  className="textarea-input"
                                  rows={3}
                                  placeholder="Notes internes"
                                  value={edit.notes_internes || ''}
                                  onChange={(e) => majEdit(cmd.id, 'notes_internes', e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="cmd-detail-footer">
                              <button className="btn btn-primary" onClick={() => sauvegarderDetails(cmd.id)} disabled={savingId === cmd.id}>
                                {savingId === cmd.id ? 'Sauvegarde…' : 'Sauvegarder les modifications'}
                              </button>
                              {cmd.statut === 'confirmee' && <button className="btn btn-ghost" onClick={() => changerStatut(cmd.id, 'prete')}>Marquer prête</button>}
                              {cmd.statut === 'prete' && <button className="btn btn-ghost" onClick={() => changerStatut(cmd.id, 'remise')}>Marquer remise</button>}
                              {cmd.statut !== 'annulee' && <button className="btn btn-ghost" onClick={() => changerStatut(cmd.id, 'annulee')}>Annuler</button>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Onglet 3 : Par slot ──────────────────────

function OngletParSlot({ evenementId, capaciteSlot }) {
  const [slots, setSlots] = useState([]);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const data = await api.get(`/evenements/${evenementId}/slots`);
      setSlots(Array.isArray(data?.slots) ? data.slots : []);
    } catch {
      setSlots([]);
    } finally {
      setChargement(false);
    }
  }, [evenementId]);

  useEffect(() => { charger(); }, [charger]);

  async function changerStatut(cmdId, statut) {
    try {
      await api.put(`/precommandes/${cmdId}/statut`, { statut });
      charger();
    } catch {}
  }

  if (chargement) return <div className="muted" style={{ padding: 24 }}>Chargement…</div>;

  if (slots.length === 0) {
    return <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Aucun slot configuré.</div>;
  }

  return (
    <div className="onglet-contenu slot-vue">
      {slots.map(slot => (
        <div key={slot.heure} className="card slot-section">
          <div className="slot-section-header">
            <div>
              <span className="slot-section-heure">{slot.heure}</span>
              <span className="muted" style={{ fontSize: 13, marginLeft: 12 }}>
                {slot.commandes?.length || 0} commande(s)
              </span>
            </div>
            <div className="slot-compteur-badge">
              <span style={{
                color: slot.nb_personnes >= capaciteSlot ? 'var(--danger)'
                  : slot.nb_personnes >= capaciteSlot * 0.8 ? 'var(--warning)'
                  : 'var(--success)',
              }}>
                {slot.nb_personnes} / {capaciteSlot}
              </span>
              <span className="muted"> personnes</span>
            </div>
          </div>

          {slot.commandes && slot.commandes.length > 0 ? (
            <table className="esc-table" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Plateau(x)</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {slot.commandes.map(cmd => (
                  <tr key={cmd.id}>
                    <td>
                      <div>{cmd.client_prenom} {cmd.client_nom}</div>
                      <div className="muted" style={{ fontSize: 11 }}>{cmd.numero_conf}</div>
                    </td>
                    <td>
                      <div>{formatListePlateaux(cmd.items) || '—'}</div>
                      {formatResumeBoissons(cmd.boissons_choisies) && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          🥤 {formatResumeBoissons(cmd.boissons_choisies)}
                        </div>
                      )}
                      {formatResumeExtras(cmd.extras) && (
                        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                          {formatResumeExtras(cmd.extras)}
                        </div>
                      )}
                      {(cmd.allergies || []).length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <span className="badge badge-danger">Allergies</span>
                        </div>
                      )}
                    </td>
                    <td><StatutBadge statut={cmd.statut} map={STATUTS_CMD} /></td>
                    <td>
                      <div className="cmd-actions">
                        {cmd.statut === 'confirmee' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => changerStatut(cmd.id, 'prete')}
                          >
                            Marquer prête
                          </button>
                        )}
                        {cmd.statut === 'prete' && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => changerStatut(cmd.id, 'remise')}
                          >
                            Marquer remise
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="muted" style={{ padding: '12px 0', fontSize: 13 }}>Aucune commande pour ce slot.</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Onglet 4 : Production ────────────────────

const PRODUITS_PROD = [
  { key: 'porc',      label: 'Porc Effiloché',  unite: 'kg cuit' },
  { key: 'brisket',   label: 'Brisket',          unite: 'kg cuit' },
  { key: 'ribs',      label: 'Côtes Levées',     unite: 'racks' },
  { key: 'saucisses', label: 'Saucisses',         unite: 'saucisses' },
];

function OngletProduction({ evenementId }) {
  const [prod, setProd] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [prodTotale, setProdTotale] = useState({ saucisses: '', porc: '' });
  const [synchroMsg, setSynchroMsg] = useState('');
  const [synchroLoading, setSynchroLoading] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const data = await api.get(`/evenements/${evenementId}/production`);
      setProd(data || null);
      const init = { saucisses: '', porc: '' };
      for (const p of (data.production || [])) {
        if (p.produit === 'saucisses') init.saucisses = p.production_totale || '';
        if (p.produit === 'porc')      init.porc      = p.production_totale || '';
      }
      setProdTotale(init);
    } catch {
      setProd(null);
    } finally {
      setChargement(false);
    }
  }, [evenementId]);

  useEffect(() => { charger(); }, [charger]);

  async function sauvegarderProduction() {
    setSynchroLoading(true);
    setSynchroMsg('');
    try {
      // Construire le tableau de production à mettre à jour
      const productionExistante = (prod?.production || []).map((item) => ({
        produit: item.produit,
        poids_portion_g: item.poids_portion_g,
        personnes_par_unite: item.personnes_par_unite,
        unite_label: item.unite_label,
        rendement_pct: item.rendement_pct,
        marge_pct: item.marge_pct,
        production_totale: item.production_totale || 0,
      }));

      const miseAJour = productionExistante.map((item) => {
        if (item.produit === 'saucisses') {
          return { ...item, production_totale: parseNombre(prodTotale.saucisses) };
        }
        if (item.produit === 'porc') {
          return { ...item, production_totale: parseNombre(prodTotale.porc) };
        }
        return item;
      });

      await api.put(`/evenements/${evenementId}/production`, { production: miseAJour });
      await api.post(`/evenements/${evenementId}/sync-extras-stock`);
      setSynchroMsg('✓ Stock extras mis à jour');
      charger();
    } catch {
      setSynchroMsg('Erreur lors de la synchronisation');
    } finally {
      setSynchroLoading(false);
    }
  }

  // Calcul temps réel des extras disponibles
  const extrasSaucisses = prodTotale.saucisses !== ''
    ? Math.max(0, Math.floor(parseNombre(prodTotale.saucisses) - Math.ceil((prod?.total_personnes || 0) / 2)))
    : null;
  const extrasPorc = prodTotale.porc !== ''
    ? Math.max(0, Math.floor((parseNombre(prodTotale.porc) * 1000 - (prod?.total_personnes || 0) * 150) / 150))
    : null;

  function badgeStock(val) {
    if (val === null) return null;
    if (val > 20) return { cls: 'badge-success', label: `${val} extras 🟢` };
    if (val > 5)  return { cls: 'badge-warning', label: `${val} extras 🟡` };
    if (val > 0)  return { cls: 'badge-danger',  label: `${val} extras 🔴` };
    return { cls: 'badge-danger', label: 'Aucun extra disponible' };
  }

  if (chargement) return <div className="muted" style={{ padding: 24 }}>Calcul en cours…</div>;

  return (
    <div className="onglet-contenu">
      {prod && (
        <div className="prod-note muted">
          Basé sur <strong style={{ color: 'var(--text)' }}>{prod.nb_commandes_confirmees}</strong> commande(s) confirmée(s) et <strong style={{ color: 'var(--text)' }}>{prod.total_personnes}</strong> personne(s).
        </div>
      )}

      <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 className="apercu-section-titre" style={{ marginBottom: 16 }}>Calculateur de production</h3>

        <table className="esc-table">
          <thead>
            <tr>
              <th>Produit</th>
              <th>Personnes</th>
              <th>Unités nettes</th>
              <th>Unités avec marge</th>
              <th>Poids cru requis</th>
              <th>Poids avec marge</th>
            </tr>
          </thead>
          <tbody>
            {(prod?.production || []).map((c) => {
              const produit = PRODUITS_PROD.find((item) => item.key === c.produit);
              return (
                <tr key={c.produit}>
                  <td><strong>{produit?.label || c.produit}</strong></td>
                  <td>{c.total_personnes ?? '—'}</td>
                  <td>{c.unites_nettes ?? '—'}</td>
                  <td><strong className="gold">{c.unites_avec_marge ?? '—'}</strong></td>
                  <td>{c.cru_necessaire_kg != null ? `${c.cru_necessaire_kg.toFixed(2)} kg` : '—'}</td>
                  <td>{c.cru_avec_marge_kg != null ? `${c.cru_avec_marge_kg.toFixed(2)} kg` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── Section Production réelle saisie ─── */}
      <div className="card" style={{ padding: '20px 24px', marginTop: 0 }}>
        <h3 className="apercu-section-titre" style={{ marginBottom: 16 }}>
          Production réelle — Calculer les extras disponibles
        </h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Saisissez les quantités réellement produites pour calculer le stock d'extras vendables.
          Basé sur <strong style={{ color: 'var(--text)' }}>{prod?.total_personnes || 0}</strong> personne{(prod?.total_personnes || 0) !== 1 ? 's' : ''} confirmée{(prod?.total_personnes || 0) !== 1 ? 's' : ''}.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* Saucisses */}
          <div className="card" style={{ padding: '16px 18px', background: 'var(--surface)' }}>
            <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Saucisses</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <input
                type="number"
                className="param-input"
                placeholder="0"
                value={prodTotale.saucisses}
                onChange={e => setProdTotale(p => ({ ...p, saucisses: e.target.value }))}
                style={{ width: 90 }}
              />
              <span className="muted" style={{ fontSize: 13 }}>saucisses produites</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Pour les plateaux : <strong>{Math.ceil((prod?.total_personnes || 0) / 2)}</strong> (calculé auto)
            </div>
            {badgeStock(extrasSaucisses) && (
              <div style={{ marginTop: 8 }}>
                <span className={`badge ${badgeStock(extrasSaucisses).cls}`}>
                  {badgeStock(extrasSaucisses).label}
                </span>
              </div>
            )}
          </div>

          {/* Porc */}
          <div className="card" style={{ padding: '16px 18px', background: 'var(--surface)' }}>
            <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Porc Effiloché</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <input
                type="number"
                className="param-input"
                placeholder="0"
                value={prodTotale.porc}
                onChange={e => setProdTotale(p => ({ ...p, porc: e.target.value }))}
                style={{ width: 90 }}
              />
              <span className="muted" style={{ fontSize: 13 }}>kg produits</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Pour les plateaux : <strong>{(((prod?.total_personnes || 0) * 150) / 1000).toFixed(1)} kg</strong> (calculé auto)
            </div>
            {badgeStock(extrasPorc) && (
              <div style={{ marginTop: 8 }}>
                <span className={`badge ${badgeStock(extrasPorc).cls}`}>
                  {badgeStock(extrasPorc).label}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn btn-primary"
            onClick={sauvegarderProduction}
            disabled={synchroLoading}
          >
            {synchroLoading ? 'Synchronisation…' : 'Sauvegarder + Synchroniser les extras'}
          </button>
          {synchroMsg && (
            <span className={synchroMsg.startsWith('✓') ? 'success' : 'danger'} style={{ fontSize: 13 }}>
              {synchroMsg}
            </span>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre" style={{ marginBottom: 16 }}>Boissons à prévoir</h3>
        <table className="esc-table">
          <thead>
            <tr>
              <th>Boisson</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(prod?.boissons_totales || {}).map(([nom, total]) => (
              <tr key={nom}>
                <td>{nom}</td>
                <td><strong>{total}</strong></td>
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              <td>
                <strong className="gold">
                  {Object.values(prod?.boissons_totales || {}).reduce((sum, val) => sum + val, 0)} boissons
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Onglet 5 : Finances ──────────────────────

function OngletFinances({ evenementId }) {
  const [finances, setFinances] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    async function charger() {
      setChargement(true);
      try {
        const data = await api.get(`/evenements/${evenementId}/finances`);
        setFinances(data);
      } catch {
        setFinances(null);
      } finally {
        setChargement(false);
      }
    }
    charger();
  }, [evenementId]);

  if (chargement) return <div className="muted" style={{ padding: 24 }}>Chargement…</div>;
  if (!finances) return <div className="muted" style={{ padding: 24 }}>Données financières indisponibles.</div>;

  const revenu_total = finances.revenus_total || 0;

  return (
    <div className="onglet-contenu">
      {/* KPIs */}
      <div className="finances-kpis">
        <div className="card kpi-fin">
          <div className="muted kpi-fin-lbl">Revenus total</div>
          <div className="kpi-fin-val gold">{fmt$(finances.revenus_total)}</div>
        </div>
        <div className="card kpi-fin">
          <div className="muted kpi-fin-lbl">Commandes</div>
          <div className="kpi-fin-val">{finances.nb_commandes || 0}</div>
        </div>
        <div className="card kpi-fin">
          <div className="muted kpi-fin-lbl">Personnes</div>
          <div className="kpi-fin-val">{finances.nb_personnes || 0}</div>
        </div>
        <div className="card kpi-fin">
          <div className="muted kpi-fin-lbl">Revenu moy./personne</div>
          <div className="kpi-fin-val">{fmt$(finances.revenu_moyen_personne)}</div>
        </div>
      </div>

      {/* Breakdown par plateau */}
      {finances.par_plateau && finances.par_plateau.length > 0 && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 className="apercu-section-titre">Par plateau</h3>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Taille</th>
                <th>Nb vendus</th>
                <th>Revenus</th>
                <th>% du total</th>
              </tr>
            </thead>
            <tbody>
              {finances.par_plateau.map(p => (
                <tr key={p.taille}>
                  <td><span className="badge badge-muted">{p.taille}</span></td>
                  <td>{p.nb_vendus}</td>
                  <td className="gold">{fmt$(p.revenus)}</td>
                  <td>
                    <div className="pct-barre-wrapper">
                      <span>{fmtPct(revenu_total > 0 ? (p.revenus / revenu_total) * 100 : 0)}</span>
                      <div className="mini-barre">
                        <div
                          className="mini-barre-fill"
                          style={{ width: `${revenu_total > 0 ? (p.revenus / revenu_total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Breakdown par slot */}
      {finances.par_slot && finances.par_slot.length > 0 && (
        <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
          <h3 className="apercu-section-titre">Par fenêtre horaire</h3>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Slot</th>
                <th>Revenus</th>
                <th>% du total</th>
              </tr>
            </thead>
            <tbody>
              {finances.par_slot.map(s => (
                <tr key={s.heure}>
                  <td><strong>{s.heure}</strong></td>
                  <td className="gold">{fmt$(s.revenus)}</td>
                  <td>
                    <div className="pct-barre-wrapper">
                      <span>{fmtPct(revenu_total > 0 ? (s.revenus / revenu_total) * 100 : 0)}</span>
                      <div className="mini-barre">
                        <div
                          className="mini-barre-fill"
                          style={{ width: `${revenu_total > 0 ? (s.revenus / revenu_total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Statut paiements */}
      {finances.paiements && (
        <div className="card" style={{ padding: '20px 24px' }}>
          <h3 className="apercu-section-titre">Statut des paiements</h3>
          <div className="paiements-grid">
            {[
              { key: 'paid',     label: 'Payés',      couleur: 'var(--success)' },
              { key: 'pending',  label: 'En attente', couleur: 'var(--warning)' },
              { key: 'refunded', label: 'Remboursés', couleur: 'var(--danger)' },
            ].map(p => (
              <div key={p.key} className="paiement-stat">
                <div className="muted" style={{ fontSize: 12 }}>{p.label}</div>
                <div style={{ color: p.couleur, fontWeight: 700, fontSize: 20 }}>
                  {finances.paiements[p.key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal modification événement ─────────────

function ModalModifierEvenement({ ev, onClose, onSauvegarde }) {
  const [form, setForm] = useState({
    nom: ev.nom || '',
    date_evenement: ev.date_evenement ? ev.date_evenement.slice(0, 10) : '',
    lieu: ev.lieu || '',
    adresse: ev.adresse || '',
    ville: ev.ville || '',
    description: ev.description || '',
    capacite_totale: ev.capacite_totale || 200,
    capacite_slot: ev.capacite_slot || 40,
    date_limite_precommande: ev.date_limite_precommande ? ev.date_limite_precommande.slice(0, 10) : '',
  });
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);

  function setChamp(k, v) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function soumettre(e) {
    e.preventDefault();
    if (!form.nom.trim() || !form.date_evenement || !form.lieu.trim()) {
      setErreur('Nom, date et lieu sont obligatoires.');
      return;
    }
    setChargement(true);
    setErreur('');
    try {
      const data = await api.put(`/evenements/${ev.id}`, form);
      onSauvegarde(data);
    } catch (err) {
      setErreur(err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setChargement(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box evenements-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-titre">Modifier l'événement</h2>
          <button className="modal-close" onClick={onClose}><Icon name="fermer" size={16} /></button>
        </div>
        <form onSubmit={soumettre} className="evenement-form">
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Nom *</label>
              <input type="text" value={form.nom} onChange={e => setChamp('nom', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <DatePicker
                selected={form.date_evenement ? new Date(form.date_evenement) : null}
                onChange={d => setChamp('date_evenement', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
              />
            </div>
            <div className="form-group">
              <label>Limite précommande</label>
              <DatePicker
                selected={form.date_limite_precommande ? new Date(form.date_limite_precommande) : null}
                onChange={d => setChamp('date_limite_precommande', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
                isClearable
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Lieu *</label>
              <input type="text" value={form.lieu} onChange={e => setChamp('lieu', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Ville</label>
              <input type="text" value={form.ville} onChange={e => setChamp('ville', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Adresse</label>
              <input type="text" value={form.adresse} onChange={e => setChamp('adresse', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Capacité totale</label>
              <input type="number" min={1} value={form.capacite_totale} onChange={e => setChamp('capacite_totale', parseInt(e.target.value) || 200)} />
            </div>
            <div className="form-group">
              <label>Capacité par slot</label>
              <input type="number" min={1} value={form.capacite_slot} onChange={e => setChamp('capacite_slot', parseInt(e.target.value) || 40)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Description</label>
              <textarea value={form.description} onChange={e => setChamp('description', e.target.value)} rows={3} />
            </div>
          </div>
          {erreur && <div className="form-erreur">{erreur}</div>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={chargement}>
              {chargement ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Onglet 6 : Paramètres ───────────────────

function OngletParametres({ ev: evProp, plateaux: plateauxProp, evenementId, onUpdateEv, onUpdatePlateaux, onUpdateProd }) {
  // ── Section Infos générales ──
  const [formInfos, setFormInfos] = useState({
    nom: evProp.nom || '',
    description: evProp.description || '',
    type: evProp.type || 'public',
    date_event: evProp.date_event ? String(evProp.date_event).slice(0, 10) : '',
    heure_debut: evProp.heure_debut || '17:00',
    heure_fin: evProp.heure_fin || '19:30',
    lieu: evProp.lieu || '',
    adresse: evProp.adresse || '',
    ville: evProp.ville || '',
    google_maps_url: evProp.google_maps_url || '',
    date_limite_precommande: evProp.date_limite_precommande
      ? String(evProp.date_limite_precommande).slice(0, 10)
      : '',
    notes: evProp.notes || '',
    message_accueil: evProp.message_accueil || '',
    message_confirmation: evProp.message_confirmation || '',
    google_maps_embed: evProp.google_maps_embed || '',
  });
  const [savingInfos, setSavingInfos] = useState(false);
  const [msgInfos, setMsgInfos] = useState(null);

  // ── Section Capacité ──
  const [formCap, setFormCap] = useState({
    capacite_totale: evProp.capacite_totale || 200,
    capacite_par_slot: evProp.capacite_par_slot || 40,
    slot_duree_min: evProp.slot_duree_min || 30,
  });
  const [savingCap, setSavingCap] = useState(false);
  const [msgCap, setMsgCap] = useState(null);
  const nbCommandes = evProp.stats?.nb_commandes || 0;

  // ── Section Plateaux ──
  const [formPlateaux, setFormPlateaux] = useState(plateauxProp.map(p => ({ ...p })));
  const [savingPlateaux, setSavingPlateaux] = useState(false);
  const [msgPlateaux, setMsgPlateaux] = useState(null);

  // ── Section Production ──
  const [formProd, setFormProd] = useState(
    Array.isArray(evProp.production) ? evProp.production.map(p => ({ ...p })) : []
  );
  const [savingProd, setSavingProd] = useState(false);
  const [msgProd, setMsgProd] = useState(null);

  // ── Section Danger ──
  const [showConfirmAnnuler, setShowConfirmAnnuler] = useState(false);
  const [savingDanger, setSavingDanger] = useState(false);
  const [msgDanger, setMsgDanger] = useState(null);

  const cloture = evProp.evenement_cloture === 1;

  function flash(setter, type, text) {
    setter({ type, text });
    setTimeout(() => setter(null), 4000);
  }

  function setInfoChamp(k, v) { setFormInfos(f => ({ ...f, [k]: v })); }
  function setCapChamp(k, v)  { setFormCap(f => ({ ...f, [k]: v })); }

  async function sauvegarderInfos(e) {
    e.preventDefault();
    setSavingInfos(true);
    try {
      const data = await api.put(`/evenements/${evenementId}`, formInfos);
      onUpdateEv(data);
      flash(setMsgInfos, 'ok', 'Informations sauvegardées.');
    } catch (err) {
      flash(setMsgInfos, 'err', err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingInfos(false);
    }
  }

  async function sauvegarderCap(e) {
    e.preventDefault();
    setSavingCap(true);
    try {
      const data = await api.put(`/evenements/${evenementId}`, formCap);
      onUpdateEv(data);
      flash(setMsgCap, 'ok', 'Capacité sauvegardée.');
    } catch (err) {
      flash(setMsgCap, 'err', err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingCap(false);
    }
  }

  async function sauvegarderPlateaux() {
    setSavingPlateaux(true);
    try {
      const data = await api.put(`/evenements/${evenementId}/plateaux`, { plateaux: formPlateaux });
      onUpdatePlateaux(Array.isArray(data) ? data : formPlateaux);
      flash(setMsgPlateaux, 'ok', 'Plateaux sauvegardés.');
    } catch (err) {
      flash(setMsgPlateaux, 'err', err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingPlateaux(false);
    }
  }

  function ajouterPlateau() {
    setFormPlateaux(prev => [
      ...prev,
      { id: null, evenement_id: parseInt(evenementId), taille: '', label: '', prix: '', actif: 1 },
    ]);
  }

  function setPlateau(i, champ, val) {
    setFormPlateaux(prev => prev.map((x, j) => j === i ? { ...x, [champ]: val } : x));
  }

  async function sauvegarderProd() {
    setSavingProd(true);
    try {
      const data = await api.put(`/evenements/${evenementId}/production`, { production: formProd });
      onUpdateProd(Array.isArray(data) ? data : formProd);
      flash(setMsgProd, 'ok', 'Paramètres de production sauvegardés.');
    } catch (err) {
      flash(setMsgProd, 'err', err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingProd(false);
    }
  }

  function setProdChamp(i, champ, val) {
    setFormProd(prev => prev.map((x, j) => j === i ? { ...x, [champ]: val } : x));
  }

  async function fermerPrecommandes() {
    setSavingDanger(true);
    try {
      const data = await api.put(`/evenements/${evenementId}`, { precommande_active: 0 });
      onUpdateEv(data);
      flash(setMsgDanger, 'ok', 'Précommandes fermées.');
    } catch (err) {
      flash(setMsgDanger, 'err', err?.message || 'Erreur.');
    } finally {
      setSavingDanger(false);
    }
  }

  async function ouvrirPrecommandes() {
    setSavingDanger(true);
    try {
      const data = await api.put(`/evenements/${evenementId}`, { precommande_active: 1 });
      onUpdateEv(data);
      flash(setMsgDanger, 'ok', 'Précommandes ouvertes.');
    } catch (err) {
      flash(setMsgDanger, 'err', err?.message || 'Erreur.');
    } finally {
      setSavingDanger(false);
    }
  }

  async function annulerEvenement() {
    setShowConfirmAnnuler(false);
    setSavingDanger(true);
    try {
      const data = await api.put(`/evenements/${evenementId}`, { statut: 'annule' });
      onUpdateEv(data);
      flash(setMsgDanger, 'ok', 'Événement annulé.');
    } catch (err) {
      flash(setMsgDanger, 'err', err?.message || 'Erreur.');
    } finally {
      setSavingDanger(false);
    }
  }

  return (
    <div className="onglet-contenu">

      {/* ── Infos générales ── */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Informations générales</h3>
        <form onSubmit={sauvegarderInfos} className="params-form">
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Nom</label>
              <input type="text" value={formInfos.nom} onChange={e => setInfoChamp('nom', e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select value={formInfos.type} onChange={e => setInfoChamp('type', e.target.value)}>
                <option value="public">Public</option>
                <option value="prive">Privé</option>
                <option value="corporatif">Corporatif</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <DatePicker
                selected={formInfos.date_event ? new Date(formInfos.date_event) : null}
                onChange={d => setInfoChamp('date_event', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Heure début</label>
              <input type="time" value={formInfos.heure_debut} onChange={e => setInfoChamp('heure_debut', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Heure fin</label>
              <input type="time" value={formInfos.heure_fin} onChange={e => setInfoChamp('heure_fin', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Lieu</label>
              <input type="text" value={formInfos.lieu} onChange={e => setInfoChamp('lieu', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Ville</label>
              <input type="text" value={formInfos.ville} onChange={e => setInfoChamp('ville', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Adresse</label>
              <input type="text" value={formInfos.adresse} onChange={e => setInfoChamp('adresse', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>URL Google Maps</label>
              <input
                type="text"
                value={formInfos.google_maps_url}
                onChange={e => setInfoChamp('google_maps_url', e.target.value)}
                placeholder="https://maps.google.com/..."
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Limite précommande</label>
              <DatePicker
                selected={formInfos.date_limite_precommande ? new Date(formInfos.date_limite_precommande) : null}
                onChange={d => setInfoChamp('date_limite_precommande', d ? d.toISOString().split('T')[0] : '')}
                dateFormat="dd MMMM yyyy"
                locale="fr"
                placeholderText="Choisir une date"
                className="datepicker-input"
                calendarClassName="datepicker-calendar"
                isClearable
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Notes internes</label>
              <textarea value={formInfos.notes} onChange={e => setInfoChamp('notes', e.target.value)} rows={3} />
            </div>
          </div>
          {msgInfos && (
            <div className={msgInfos.type === 'ok' ? 'msg-success' : 'msg-erreur'}>{msgInfos.text}</div>
          )}
          <div className="params-save-row">
            <button type="submit" className="btn btn-primary" disabled={savingInfos}>
              {savingInfos ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Messages & Visuel ── */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Messages & Visuel (formulaire public)</h3>
        <div className="params-form">
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Message d'accueil</label>
              <textarea
                value={formInfos.message_accueil}
                onChange={e => setInfoChamp('message_accueil', e.target.value)}
                rows={2}
                placeholder="Bienvenue ! Choisissez vos plateaux ci-dessous."
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Affiché en haut du formulaire public
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Message de confirmation</label>
              <textarea
                value={formInfos.message_confirmation}
                onChange={e => setInfoChamp('message_confirmation', e.target.value)}
                rows={2}
                placeholder="Merci ! On a hâte de vous recevoir 🔥"
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Affiché après paiement et dans l'email de confirmation
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group form-group-full">
              <label>Google Maps Embed URL</label>
              <input
                type="text"
                value={formInfos.google_maps_embed}
                onChange={e => setInfoChamp('google_maps_embed', e.target.value)}
                placeholder="https://www.google.com/maps/embed?pb=..."
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                URL de l'iframe Google Maps (commence par https://www.google.com/maps/embed)
              </div>
            </div>
          </div>
          {formInfos.google_maps_embed && (
            <div style={{ marginTop: 12 }}>
              <iframe
                src={formInfos.google_maps_embed}
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8 }}
                allowFullScreen
                loading="lazy"
                title="Aperçu carte"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Capacité ── */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Capacité</h3>
        {nbCommandes > 0 && (
          <div className="params-warning">
            ⚠️ Modifier la capacité affecte les slots disponibles ({nbCommandes} commande(s) existante(s))
          </div>
        )}
        <form onSubmit={sauvegarderCap} className="params-form">
          <div className="form-row">
            <div className="form-group">
              <label>Capacité totale (personnes)</label>
              <input
                type="number" min={1}
                value={formCap.capacite_totale}
                onChange={e => setCapChamp('capacite_totale', parseInt(e.target.value) || 200)}
              />
            </div>
            <div className="form-group">
              <label>Capacité par slot (personnes)</label>
              <input
                type="number" min={1}
                value={formCap.capacite_par_slot}
                onChange={e => setCapChamp('capacite_par_slot', parseInt(e.target.value) || 40)}
              />
            </div>
            <div className="form-group">
              <label>Durée des slots (minutes)</label>
              <input
                type="number" min={1}
                value={formCap.slot_duree_min}
                onChange={e => setCapChamp('slot_duree_min', parseInt(e.target.value) || 30)}
              />
            </div>
          </div>
          {msgCap && (
            <div className={msgCap.type === 'ok' ? 'msg-success' : 'msg-erreur'}>{msgCap.text}</div>
          )}
          <div className="params-save-row">
            <button type="submit" className="btn btn-primary" disabled={savingCap}>
              {savingCap ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Plateaux ── */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="apercu-section-titre" style={{ margin: 0 }}>Plateaux</h3>
          <button className="btn btn-ghost btn-sm" type="button" onClick={ajouterPlateau}>+ Ajouter</button>
        </div>
        <table className="esc-table">
          <thead>
            <tr>
              <th>Taille (pers.)</th>
              <th>Libellé</th>
              <th>Prix ($)</th>
              <th>Actif</th>
            </tr>
          </thead>
          <tbody>
            {formPlateaux.map((p, i) => (
              <tr key={p.id || `new-${i}`}>
                <td>
                  <input
                    type="number" min={1}
                    className="param-input"
                    value={p.taille}
                    onChange={e => setPlateau(i, 'taille', parseInt(e.target.value) || '')}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="param-input"
                    style={{ width: 110 }}
                    value={p.label}
                    onChange={e => setPlateau(i, 'label', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number" min={0} step={0.01}
                    className="param-input"
                    value={p.prix}
                    onChange={e => setPlateau(i, 'prix', parseFloat(e.target.value) || '')}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={p.actif === 1 || p.actif === true}
                    onChange={e => setPlateau(i, 'actif', e.target.checked ? 1 : 0)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {msgPlateaux && (
          <div className={msgPlateaux.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 12 }}>
            {msgPlateaux.text}
          </div>
        )}
        <div className="params-save-row">
          <button className="btn btn-primary" type="button" onClick={sauvegarderPlateaux} disabled={savingPlateaux}>
            {savingPlateaux ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* ── Production ── */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Paramètres de production</h3>
        {formProd.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>Aucun paramètre de production configuré.</div>
        ) : (
          <table className="esc-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Rendement (%)</th>
                <th>Marge (%)</th>
                <th>Personnes/unité</th>
                <th>Poids portion (g)</th>
              </tr>
            </thead>
            <tbody>
              {formProd.map((p, i) => (
                <tr key={p.id || p.produit}>
                  <td><strong style={{ textTransform: 'capitalize' }}>{p.produit}</strong></td>
                  <td>
                    <input
                      type="number" min={0} max={100} step={0.1}
                      className="param-input"
                      value={p.rendement_pct ?? ''}
                      onChange={e => setProdChamp(i, 'rendement_pct', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="number" min={0} max={100} step={0.1}
                      className="param-input"
                      value={p.marge_pct ?? ''}
                      onChange={e => setProdChamp(i, 'marge_pct', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="number" min={0} step={0.1}
                      className="param-input"
                      value={p.personnes_par_unite ?? ''}
                      onChange={e => setProdChamp(i, 'personnes_par_unite', parseFloat(e.target.value) || null)}
                    />
                  </td>
                  <td>
                    <input
                      type="number" min={0}
                      className="param-input"
                      value={p.poids_portion_g ?? ''}
                      onChange={e => setProdChamp(i, 'poids_portion_g', parseFloat(e.target.value) || null)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {msgProd && (
          <div className={msgProd.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 12 }}>
            {msgProd.text}
          </div>
        )}
        {formProd.length > 0 && (
          <div className="params-save-row">
            <button className="btn btn-primary" type="button" onClick={sauvegarderProd} disabled={savingProd}>
              {savingProd ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        )}
      </div>

      {/* ── Zone de danger ── */}
      <div className="card params-danger-zone">
        <h3 className="apercu-section-titre" style={{ color: 'var(--danger)', borderBottomColor: 'color-mix(in srgb, var(--danger) 30%, transparent)' }}>Zone de danger</h3>
        <div className="params-danger-item">
          <div>
            <strong>Précommandes</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              {evProp.precommande_active === 1
                ? 'Les précommandes sont ouvertes — les clients peuvent commander.'
                : 'Les précommandes sont fermées — aucun nouveau client ne peut commander.'}
            </div>
          </div>
          {evProp.precommande_active === 0 ? (
            <button
              className="btn btn-primary"
              type="button"
              onClick={ouvrirPrecommandes}
              disabled={savingDanger || cloture}
            >
              Ouvrir les précommandes
            </button>
          ) : (
            <button
              className="btn btn-danger"
              type="button"
              onClick={fermerPrecommandes}
              disabled={savingDanger || cloture}
            >
              Fermer les précommandes
            </button>
          )}
        </div>
        <div className="params-danger-item" style={{ marginTop: 16 }}>
          <div>
            <strong>Annuler l'événement</strong>
            <div className="muted" style={{ fontSize: 13 }}>Passe le statut à "Annulé".</div>
          </div>
          <button
            className="btn btn-danger"
            type="button"
            onClick={() => setShowConfirmAnnuler(true)}
            disabled={savingDanger || cloture}
          >
            Annuler l'événement
          </button>
        </div>
        {msgDanger && (
          <div className={msgDanger.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 16 }}>
            {msgDanger.text}
          </div>
        )}
      </div>

      {/* Modal confirmation annulation */}
      {showConfirmAnnuler && (
        <div className="modal-overlay" onClick={() => setShowConfirmAnnuler(false)}>
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-titre">Confirmer l'annulation</h2>
              <button className="modal-close" onClick={() => setShowConfirmAnnuler(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <div style={{ padding: '0 24px 24px' }}>
              <p style={{ color: 'var(--text)', marginBottom: 20 }}>
                Êtes-vous sûr de vouloir annuler cet événement ? Cette action est difficile à inverser.
              </p>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setShowConfirmAnnuler(false)}>Non, garder</button>
                <button className="btn btn-danger" onClick={annulerEvenement}>Oui, annuler l'événement</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Plateaux configurables ───────────

const PRODUITS_OPTIONS = [
  'Porc Effiloché', 'Brisket', 'Côtes Levées', 'Saucisses Jalapeño Cheddar',
  'Brisket Burnt Ends', 'Saumon Fumé', 'Sauce BBQ',
];

const SIDES_DEFAUT = [
  'Salade brocoli', 'Mac n cheese', 'Banana pudding',
  'Pickles & oignons', 'Pain maison', 'Sauce BBQ maison',
];

function OngletPlateaux({ evenementId, plateaux, onUpdatePlateaux }) {
  const [items, setItems] = useState(plateaux.map(p => ({ ...p })));
  const [editId, setEditId] = useState(null); // id du plateau en cours d'édition (null = aucun, 'new' = nouveau)
  const [formP, setFormP] = useState(null);
  const [sauvegarde, setSauvegarde] = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [msg, setMsg] = useState(null);
  const [sideCustom, setSideCustom] = useState('');

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }

  function ouvrirEdition(plateau) {
    setFormP({
      label:          plateau.label || '',
      prix:           plateau.prix  || '',
      nb_personnes:   plateau.nb_personnes || 1,
      description:    plateau.description || '',
      actif:          plateau.actif !== 0,
      visible_public: plateau.visible_public !== 0,
      sides_inclus:   Array.isArray(plateau.sides_inclus) ? [...plateau.sides_inclus] : [...SIDES_DEFAUT],
      contenu:        Array.isArray(plateau.contenu) ? plateau.contenu.map(c => ({ ...c })) : [],
      badge_texte:    plateau.badge_texte || '',
      couleur_badge:  plateau.couleur_badge || '#c8941a',
      ordre:          plateau.ordre || 0,
    });
    setEditId(plateau.id || 'new');
    setSideCustom('');
  }

  function ouvrirNouveau() {
    ouvrirEdition({
      label: '', prix: '', nb_personnes: 1, description: '',
      actif: true, visible_public: true,
      sides_inclus: [...SIDES_DEFAUT], contenu: [],
    });
    setEditId('new');
  }

  function setChamp(k, v) { setFormP(f => ({ ...f, [k]: v })); }

  function toggleSide(side) {
    setFormP(f => ({
      ...f,
      sides_inclus: f.sides_inclus.includes(side)
        ? f.sides_inclus.filter(s => s !== side)
        : [...f.sides_inclus, side],
    }));
  }

  function ajouterSideCustom() {
    if (!sideCustom.trim()) return;
    setFormP(f => ({ ...f, sides_inclus: [...f.sides_inclus, sideCustom.trim()] }));
    setSideCustom('');
  }

  function ajouterContenu() {
    setFormP(f => ({
      ...f,
      contenu: [...f.contenu, { produit: PRODUITS_OPTIONS[0], quantite: 150, unite: 'g', label: '' }],
    }));
  }

  function setContenuItem(i, k, v) {
    setFormP(f => ({
      ...f,
      contenu: f.contenu.map((c, j) => j === i ? { ...c, [k]: v } : c),
    }));
  }

  function supprimerContenuItem(i) {
    setFormP(f => ({ ...f, contenu: f.contenu.filter((_, j) => j !== i) }));
  }

  async function sauvegarderPlateau() {
    if (!formP.label.trim() || !formP.prix) {
      flash('err', 'Label et prix sont requis.');
      return;
    }
    setSauvegarde(true);
    try {
      let result;
      const payload = { ...formP, prix: parseFloat(formP.prix) };
      if (editId === 'new') {
        result = await api.post(`/evenements/${evenementId}/plateaux`, payload);
        const newItems = [...items, result];
        setItems(newItems);
        onUpdatePlateaux(newItems);
      } else {
        result = await api.put(`/evenements/plateaux/${editId}`, payload);
        const newItems = items.map(p => p.id === editId ? result : p);
        setItems(newItems);
        onUpdatePlateaux(newItems);
      }
      setEditId(null);
      setFormP(null);
      flash('ok', 'Plateau sauvegardé.');
    } catch (err) {
      flash('err', err?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSauvegarde(false);
    }
  }

  async function supprimerPlateau(id) {
    setSuppression(id);
    try {
      await api.delete(`/evenements/plateaux/${id}`);
      const newItems = items.filter(p => p.id !== id);
      setItems(newItems);
      onUpdatePlateaux(newItems);
      flash('ok', 'Plateau supprimé.');
    } catch (err) {
      flash('err', err?.message || 'Erreur.');
    } finally {
      setSuppression(null);
    }
  }

  async function toggleActif(plateau) {
    try {
      const result = await api.patch(`/evenements/plateaux/${plateau.id}/toggle`);
      const newItems = items.map(p => p.id === plateau.id ? { ...p, actif: result.actif } : p);
      setItems(newItems);
      onUpdatePlateaux(newItems);
    } catch {}
  }

  const sidesConnus = [...new Set([
    ...SIDES_DEFAUT,
    ...(formP?.sides_inclus || []),
  ])];

  return (
    <div className="onglet-contenu">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="muted" style={{ fontSize: 13 }}>
          {items.length} plateau(x) configuré(s) pour cet événement
        </div>
        <button className="btn btn-primary btn-sm" onClick={ouvrirNouveau}>+ Nouveau plateau</button>
      </div>

      {/* Liste des plateaux */}
      <div className="plateaux-liste">
        {items.length === 0 && (
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Aucun plateau configuré. Créez-en un pour commencer.
          </div>
        )}
        {items.map(p => (
          <div key={p.id} className={`card plateau-card ${!p.actif ? 'plateau-inactif' : ''}`}>
            <div className="plateau-card-header">
              <div>
                <strong className="plateau-nom">{p.label}</strong>
                {p.nb_personnes > 1 && (
                  <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                    ({p.nb_personnes} personnes)
                  </span>
                )}
                <span className="gold" style={{ marginLeft: 12, fontWeight: 700 }}>
                  {fmt$(p.prix)}
                </span>
                <span className={`badge ${p.actif ? 'badge-success' : 'badge-muted'}`} style={{ marginLeft: 8 }}>
                  {p.actif ? 'Actif' : 'Inactif'}
                </span>
                {p.badge_texte && (
                  <span className="badge" style={{ marginLeft: 6, background: p.couleur_badge || '#c8941a', color: '#fff', fontSize: 10 }}>
                    {p.badge_texte}
                  </span>
                )}
              </div>
              <div className="plateau-card-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => ouvrirEdition(p)}>✏️</button>
                <button
                  className={`btn btn-ghost btn-sm`}
                  onClick={() => toggleActif(p)}
                  title={p.actif ? 'Désactiver' : 'Activer'}
                >
                  {p.actif ? '🔕' : '✅'}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => supprimerPlateau(p.id)}
                  disabled={suppression === p.id}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Contenu du plateau */}
            {Array.isArray(p.contenu) && p.contenu.length > 0 && (
              <div className="plateau-contenu">
                {p.contenu.map((c, i) => (
                  <span key={i} className="plateau-contenu-item">
                    {c.label || `${c.quantite}${c.unite} ${c.produit}`}
                  </span>
                ))}
              </div>
            )}

            {/* Sides */}
            {Array.isArray(p.sides_inclus) && p.sides_inclus.length > 0 && (
              <div className="plateau-sides muted" style={{ fontSize: 12, marginTop: 6 }}>
                Accompagnements : {p.sides_inclus.join(' · ')}
              </div>
            )}
          </div>
        ))}
      </div>

      {msg && (
        <div className={msg.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 12 }}>
          {msg.text}
        </div>
      )}

      {/* Modal édition plateau */}
      {editId && formP && (
        <div className="modal-overlay" onClick={() => { setEditId(null); setFormP(null); }}>
          <div className="modal-box plateau-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-titre">{editId === 'new' ? 'Nouveau plateau' : 'Modifier le plateau'}</h2>
              <button className="modal-close" onClick={() => { setEditId(null); setFormP(null); }}><Icon name="fermer" size={16} /></button>
            </div>
            <div className="plateau-form">

              <div className="form-row">
                <div className="form-group">
                  <label>Nom du plateau *</label>
                  <input type="text" value={formP.label} onChange={e => setChamp('label', e.target.value)} placeholder="Ex : Plateau Événement" />
                </div>
                <div className="form-group">
                  <label>Nombre de personnes</label>
                  <input type="number" min={1} value={formP.nb_personnes} onChange={e => setChamp('nb_personnes', parseInt(e.target.value) || 1)} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix *</label>
                  <div className="input-suffix">
                    <input type="number" min={0} step="0.01" value={formP.prix} onChange={e => setChamp('prix', e.target.value)} placeholder="45.00" />
                    <span>$</span>
                  </div>
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
                    <label className="radio-option" style={{ border: 'none', padding: 0 }}>
                      <input type="checkbox" checked={formP.actif} onChange={e => setChamp('actif', e.target.checked)} />
                      <span>Actif</span>
                    </label>
                    <label className="radio-option" style={{ border: 'none', padding: 0 }}>
                      <input type="checkbox" checked={formP.visible_public} onChange={e => setChamp('visible_public', e.target.checked)} />
                      <span>Visible (public)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Badge (optionnel)</label>
                  <input type="text" value={formP.badge_texte} onChange={e => setChamp('badge_texte', e.target.value)} placeholder="ex: POPULAIRE" />
                </div>
                <div className="form-group">
                  <label>Couleur badge</label>
                  <input type="color" value={formP.couleur_badge} onChange={e => setChamp('couleur_badge', e.target.value)} style={{ height: 38, width: '100%', padding: 4 }} />
                </div>
                <div className="form-group">
                  <label>Ordre d'affichage</label>
                  <input type="number" min={0} value={formP.ordre} onChange={e => setChamp('ordre', parseInt(e.target.value) || 0)} />
                </div>
              </div>

              {/* Contenu du plateau */}
              <div className="plateau-section-titre">Contenu du plateau</div>
              {formP.contenu.map((c, i) => (
                <div key={i} className="form-row" style={{ alignItems: 'center', gap: 8 }}>
                  <div className="form-group" style={{ flex: 2 }}>
                    <select value={c.produit} onChange={e => setContenuItem(i, 'produit', e.target.value)} style={{ width: '100%' }}>
                      {PRODUITS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <input type="number" min={0} step="0.5" value={c.quantite} onChange={e => setContenuItem(i, 'quantite', parseFloat(e.target.value) || 0)} placeholder="Quantité" />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <select value={c.unite} onChange={e => setContenuItem(i, 'unite', e.target.value)}>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="unité">unité</option>
                      <option value="rack">rack</option>
                      <option value="lien">lien</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 2 }}>
                    <input type="text" value={c.label} onChange={e => setContenuItem(i, 'label', e.target.value)} placeholder="Label affiché (optionnel)" />
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => supprimerContenuItem(i)} style={{ flexShrink: 0, marginTop: 0 }}>✕</button>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={ajouterContenu} style={{ marginBottom: 16 }}>+ Ajouter un item</button>

              {/* Sides */}
              <div className="plateau-section-titre">Accompagnements inclus</div>
              <div className="sides-grille">
                {sidesConnus.map(s => (
                  <label key={s} className="side-checkbox">
                    <input
                      type="checkbox"
                      checked={formP.sides_inclus.includes(s)}
                      onChange={() => toggleSide(s)}
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
              <div className="side-custom-row">
                <input
                  type="text"
                  value={sideCustom}
                  onChange={e => setSideCustom(e.target.value)}
                  placeholder="Ajouter un accompagnement personnalisé…"
                  onKeyDown={e => e.key === 'Enter' && ajouterSideCustom()}
                />
                <button className="btn btn-ghost btn-sm" onClick={ajouterSideCustom}>+ Ajouter</button>
              </div>

              <div className="modal-actions" style={{ paddingTop: 16 }}>
                <button className="btn btn-ghost" onClick={() => { setEditId(null); setFormP(null); }}>Annuler</button>
                <button className="btn btn-primary" onClick={sauvegarderPlateau} disabled={sauvegarde}>
                  {sauvegarde ? 'Sauvegarde…' : editId === 'new' ? 'Créer le plateau' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Client Corporatif ─────────────────

function OngletClientCorpo({ ev, evenementId, onUpdateEv }) {
  const [envoi, setEnvoi] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cloture, setCloture] = useState(false);
  const [msgCloture, setMsgCloture] = useState(null);
  const [copied, setCopied] = useState(false);

  const PUBLIC_FORM_URL = process.env.REACT_APP_PUBLIC_FORM_URL || 'http://localhost:3002';
  const lienCorpo = ev.client_corpo_token
    ? `${PUBLIC_FORM_URL}/corpo/${ev.client_corpo_token}`
    : null;

  const montant_total   = ev.montant_corpo || 0;
  const acompte_pct     = ev.acompte_pct || 0;
  const acompte_montant = Math.round(montant_total * acompte_pct / 100 * 100) / 100;
  const solde_montant   = Math.round((montant_total - acompte_montant) * 100) / 100;

  async function envoyerLien() {
    setEnvoi(true);
    setMsg(null);
    try {
      await api.post(`/evenements/${evenementId}/envoyer-lien-corpo`);
      setMsg({ type: 'ok', text: `Lien envoyé à ${ev.client_corpo_email}` });
    } catch (err) {
      setMsg({ type: 'err', text: err?.message || 'Erreur lors de l\'envoi.' });
    } finally {
      setEnvoi(false);
      setTimeout(() => setMsg(null), 5000);
    }
  }

  async function cloturer() {
    setCloture(true);
    setMsgCloture(null);
    try {
      const data = await api.post(`/evenements/${evenementId}/cloturer`);
      onUpdateEv({ statut: 'termine', evenement_cloture: 1 });
      setMsgCloture({ type: 'ok', text: `Clôturé ! Revenus : ${fmt$(data.revenus_total)}` });
    } catch (err) {
      setMsgCloture({ type: 'err', text: err?.message || 'Erreur.' });
    } finally {
      setCloture(false);
    }
  }

  function copierLien() {
    if (!lienCorpo) return;
    navigator.clipboard.writeText(lienCorpo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="onglet-contenu">

      {/* Infos client */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 className="apercu-section-titre">Client corporatif</h3>
        <div className="apercu-infos">
          <div className="apercu-ligne">
            <span className="muted">Entreprise</span>
            <span>{ev.client_corpo_entreprise || '—'}</span>
          </div>
          <div className="apercu-ligne">
            <span className="muted">Contact</span>
            <span>{ev.client_corpo_nom || '—'}</span>
          </div>
          <div className="apercu-ligne">
            <span className="muted">Email</span>
            <span>{ev.client_corpo_email || '—'}</span>
          </div>
          <div className="apercu-ligne">
            <span className="muted">Téléphone</span>
            <span>{ev.client_corpo_tel || '—'}</span>
          </div>
          <div className="apercu-ligne">
            <span className="muted">Mode paiement</span>
            <span>
              {{
                unique:     'Paiement unique',
                individuel: 'Paiement individuel',
                hybride:    'Hybride',
              }[ev.type_paiement] || '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Paiement */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 className="apercu-section-titre">Paiement</h3>
        <div className="apercu-infos">
          <div className="apercu-ligne">
            <span className="muted">Montant total</span>
            <span className="gold" style={{ fontWeight: 700, fontSize: 18 }}>{fmt$(montant_total)}</span>
          </div>
          {acompte_pct > 0 && (
            <>
              <div className="apercu-ligne">
                <span className="muted">Acompte ({acompte_pct}%)</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>{fmt$(acompte_montant)}</span>
                  {ev.acompte_paye ? (
                    <span className="badge badge-success">Payé le {ev.acompte_date ? ev.acompte_date.slice(0, 10) : '—'}</span>
                  ) : (
                    <span className="badge badge-muted">En attente</span>
                  )}
                </div>
              </div>
              <div className="apercu-ligne">
                <span className="muted">Solde</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>{fmt$(solde_montant)}</span>
                  {ev.solde_paye ? (
                    <span className="badge badge-success">Payé le {ev.solde_date ? ev.solde_date.slice(0, 10) : '—'}</span>
                  ) : (
                    <span className="badge badge-muted">En attente</span>
                  )}
                </div>
              </div>
            </>
          )}
          {ev.acompte_paye && ev.solde_paye && (
            <div className="apercu-ligne">
              <span></span>
              <span className="badge badge-success" style={{ fontSize: 14, padding: '6px 12px' }}>
                Paiement complet
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Lien page corporative */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 className="apercu-section-titre">Lien page cliente</h3>
        {lienCorpo ? (
          <>
            <div className="corpo-lien-box">
              <span className="corpo-lien-url muted">{lienCorpo}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={copierLien}>
                  {copied ? 'Copié !' : 'Copier'}
                </button>
                <a href={lienCorpo} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                  ↗ Ouvrir
                </a>
              </div>
            </div>
            {ev.client_corpo_email && (
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={envoyerLien} disabled={envoi}>
                  {envoi ? 'Envoi…' : `Envoyer par email a ${ev.client_corpo_email}`}
                </button>
              </div>
            )}
            {msg && (
              <div className={msg.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 10 }}>
                {msg.text}
              </div>
            )}
          </>
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>
            Token corporatif non généré. Vérifiez que le type est bien "Corporatif" dans les paramètres.
          </div>
        )}
      </div>

      {/* Clôture */}
      {!ev.evenement_cloture && (
        <div className="card params-danger-zone">
          <h3 className="apercu-section-titre">Clôturer l'événement</h3>
          <div className="params-danger-item">
            <div>
              <div className="muted" style={{ fontSize: 13 }}>
                Marque l'événement comme terminé et crée les écritures comptables.
              </div>
            </div>
            <button className="btn btn-primary" onClick={cloturer} disabled={cloture}>
              {cloture ? 'Clôture…' : "Clôturer l'événement"}
            </button>
          </div>
          {msgCloture && (
            <div className={msgCloture.type === 'ok' ? 'msg-success' : 'msg-erreur'} style={{ marginTop: 12 }}>
              {msgCloture.text}
            </div>
          )}
        </div>
      )}
      {ev.evenement_cloture && (
        <div className="card" style={{ padding: '20px 24px' }}>
          <div className="msg-success">Événement clôturé — Revenus enregistrés dans le journal comptable.</div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Extras — stocks ──────────────────

function OngletExtras({ evenementId }) {
  const [extras, setExtras]       = useState([]);
  const [chargement, setChargement] = useState(true);
  const [editStock, setEditStock] = useState({}); // { [id]: string }
  const [saving, setSaving]       = useState(null);
  const [msg, setMsg]             = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const data = await api.get(`/evenements/${evenementId}/extras`);
      setExtras(Array.isArray(data) ? data : []);
    } catch {
      setExtras([]);
    } finally {
      setChargement(false);
    }
  }, [evenementId]);

  useEffect(() => { charger(); }, [charger]);

  async function sauvegarderStock(extra) {
    const val = editStock[extra.id];
    if (val === undefined) return;
    setSaving(extra.id);
    setMsg('');
    try {
      await api.put(`/evenements/extras/${extra.id}`, {
        stock_total: val === '' ? null : parseInt(val),
      });
      setEditStock(s => { const n = { ...s }; delete n[extra.id]; return n; });
      charger();
      setMsg(`Stock mis à jour pour ${extra.nom}`);
    } catch {
      setMsg('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  }

  if (chargement) return <div className="muted" style={{ padding: 24 }}>Chargement…</div>;

  const filtres = { extra_viande: '🔥 BBQ', sauce: '🍯 Sauces', boisson: '🥤 Boissons', boisson_incluse: '🥤 Boissons incluses' };

  return (
    <div className="onglet-contenu">
      {msg && <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>{msg}</div>}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="esc-table">
          <thead>
            <tr>
              <th>Extra</th>
              <th>Catégorie</th>
              <th>Badge</th>
              <th>Stock total</th>
              <th>Vendus</th>
              <th>Disponibles</th>
              <th>Lié production</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {extras.map(e => {
              const dispo = e.stock_total !== null ? Math.max(0, e.stock_total - (e.stock_vendu || 0)) : null;
              const enEdit = editStock[e.id] !== undefined;
              return (
                <tr key={e.id}>
                  <td>
                    <span style={{ marginRight: 6 }}>{e.emoji}</span>
                    <strong>{e.nom}</strong>
                    {e.actif === 0 && <span className="badge badge-muted" style={{ marginLeft: 8, fontSize: 10 }}>Inactif</span>}
                  </td>
                  <td className="muted">{filtres[e.categorie] || e.categorie}</td>
                  <td>{e.badge_texte ? <span className="badge badge-gold" style={{ fontSize: 10 }}>{e.badge_texte}</span> : <span className="muted">—</span>}</td>
                  <td>
                    {enEdit ? (
                      <input
                        type="number"
                        className="param-input"
                        placeholder="∞"
                        value={editStock[e.id]}
                        onChange={ev => setEditStock(s => ({ ...s, [e.id]: ev.target.value }))}
                        style={{ width: 70 }}
                        autoFocus
                      />
                    ) : (
                      <span
                        style={{ cursor: 'pointer', borderBottom: '1px dashed var(--border)' }}
                        onClick={() => setEditStock(s => ({ ...s, [e.id]: e.stock_total !== null ? String(e.stock_total) : '' }))}
                        title="Cliquer pour modifier"
                      >
                        {e.stock_total !== null ? e.stock_total : '∞'}
                      </span>
                    )}
                  </td>
                  <td className="muted">{e.stock_vendu || 0}</td>
                  <td>
                    {dispo === null
                      ? <span className="muted">∞</span>
                      : <span className={dispo > 20 ? 'success' : dispo > 5 ? '' : 'danger'}><strong>{dispo}</strong></span>
                    }
                  </td>
                  <td>
                    {e.lie_production
                      ? <span className="badge badge-success" style={{ fontSize: 10 }}>✓ Auto</span>
                      : e.stock_total !== null
                        ? <span className="badge badge-muted" style={{ fontSize: 10 }}>Manuel</span>
                        : <span className="muted" style={{ fontSize: 12 }}>∞ Illimité</span>
                    }
                    {e.produit_lie && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>({e.produit_lie})</span>}
                  </td>
                  <td>
                    {enEdit ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => sauvegarderStock(e)}
                          disabled={saving === e.id}
                        >
                          {saving === e.id ? '…' : '✓'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditStock(s => { const n = { ...s }; delete n[e.id]; return n; })}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditStock(s => ({ ...s, [e.id]: e.stock_total !== null ? String(e.stock_total) : '' }))}
                      >
                        ✏️
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Onglet Slots ──────────────────────────────
function OngletSlots({ evenementId, ev }) {
  const [slots, setSlots] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [slotEdit, setSlotEdit] = useState(null);
  const [formSlot, setFormSlot] = useState({ heure_debut: '', heure_fin: '', capacite: 40, statut: 'ouvert', label_custom: '', notes: '' });
  const [saving, setSaving] = useState(false);

  async function charger() {
    setChargement(true);
    try {
      const data = await api.get(`/evenements/${evenementId}/slots`);
      setSlots(data.slots || []);
    } catch (e) { console.error(e); }
    finally { setChargement(false); }
  }

  useEffect(() => { charger(); }, [evenementId]);

  function ouvrirNouveauSlot() {
    setSlotEdit(null);
    setFormSlot({ heure_debut: '', heure_fin: '', capacite: ev?.capacite_par_slot || 40, statut: 'ouvert', label_custom: '', notes: '' });
    setShowModal(true);
  }

  function ouvrirEditerSlot(slot) {
    setSlotEdit(slot);
    setFormSlot({ heure_debut: slot.heure_debut, heure_fin: slot.heure_fin, capacite: slot.capacite, statut: slot.statut, label_custom: slot.label_custom || '', notes: slot.notes || '' });
    setShowModal(true);
  }

  async function sauvegarderSlot() {
    setSaving(true);
    try {
      if (slotEdit) {
        await api.put(`/evenements/slots/${slotEdit.id}`, formSlot);
      } else {
        await api.post(`/evenements/${evenementId}/slots`, formSlot);
      }
      setShowModal(false);
      charger();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  }

  async function supprimerSlot(slot) {
    if (!window.confirm(`Supprimer le slot ${slot.heure_debut} ?`)) return;
    try {
      await api.delete(`/evenements/slots/${slot.id}`);
      charger();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erreur lors de la suppression');
    }
  }

  async function changerStatut(slot, action) {
    try {
      await api.patch(`/evenements/slots/${slot.id}/${action}`);
      charger();
    } catch (e) { alert(e?.response?.data?.error || 'Erreur'); }
  }

  async function generer(forcer = false) {
    try {
      await api.post(`/evenements/${evenementId}/slots/generer`, { forcer });
      charger();
    } catch (e) {
      const err = e?.response?.data;
      if (err?.nb_reservations > 0 && !forcer) {
        if (window.confirm(`${err.error}\n\nForcer la regénération ?`)) {
          generer(true);
        }
      } else {
        alert(err?.error || 'Erreur');
      }
    }
  }

  const statutLabel = { ouvert: '● Ouvert', bloque: '🔒 Bloqué', vip: '⭐ VIP' };
  const statutCls   = { ouvert: 'success', bloque: 'danger', vip: 'gold' };

  return (
    <div className="onglet-contenu">
      <div className="card" style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="apercu-section-titre" style={{ margin: 0 }}>Plages horaires</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => generer(false)}>
            <Icon name="rafraichir" size={14} /> Regénérer auto
          </button>
            <button className="btn btn-primary btn-sm" onClick={ouvrirNouveauSlot}>+ Ajouter un slot</button>
          </div>
        </div>

        {chargement ? (
          <div className="muted">Chargement…</div>
        ) : slots.length === 0 ? (
          <div className="muted">Aucun slot configuré.</div>
        ) : (
          <table className="esc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Créneau</th>
                <th>Capacité</th>
                <th>Réservations</th>
                <th>Statut</th>
                <th>Label</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {slots.map(slot => (
                <tr key={slot.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{slot.heure_debut} – {slot.heure_fin}</td>
                  <td>{slot.capacite} pers.</td>
                  <td>{slot.nb_personnes || 0} / {slot.capacite}</td>
                  <td><span className={statutCls[slot.statut] || 'muted'}>{statutLabel[slot.statut] || slot.statut}</span></td>
                  <td className="muted">{slot.label_custom || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => ouvrirEditerSlot(slot)} title="Modifier">✏️</button>
                      {slot.statut !== 'bloque' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => changerStatut(slot, 'bloquer')} title="Bloquer">🔒</button>
                      )}
                      {slot.statut !== 'vip' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => changerStatut(slot, 'vip')} title="VIP">⭐</button>
                      )}
                      {slot.statut !== 'ouvert' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => { api.put(`/evenements/slots/${slot.id}`, { statut: 'ouvert' }).then(charger); }} title="Réouvrir">🔓</button>
                      )}
                      <button className="btn btn-danger btn-sm" onClick={() => supprimerSlot(slot)} title="Supprimer">✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal-contenu" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-titre">{slotEdit ? 'Modifier le slot' : 'Nouveau slot'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Heure début</label>
                  <input type="time" value={formSlot.heure_debut} onChange={e => setFormSlot(f => ({ ...f, heure_debut: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Heure fin</label>
                  <input type="time" value={formSlot.heure_fin} onChange={e => setFormSlot(f => ({ ...f, heure_fin: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Capacité</label>
                  <input type="number" min={1} value={formSlot.capacite} onChange={e => setFormSlot(f => ({ ...f, capacite: parseInt(e.target.value) || 40 }))} />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={formSlot.statut} onChange={e => setFormSlot(f => ({ ...f, statut: e.target.value }))}>
                    <option value="ouvert">● Ouvert</option>
                    <option value="bloque">🔒 Bloqué</option>
                    <option value="vip">⭐ VIP</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Label personnalisé (optionnel)</label>
                <input type="text" placeholder="ex: Accès VIP" value={formSlot.label_custom} onChange={e => setFormSlot(f => ({ ...f, label_custom: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input type="text" value={formSlot.notes} onChange={e => setFormSlot(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={sauvegarderSlot} disabled={saving}>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Sides ──────────────────────────────
function OngletSides({ evenementId }) {
  const [sides, setSides] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [formNom, setFormNom] = useState('');
  const [formEmoji, setFormEmoji] = useState('🥗');
  const [editId, setEditId] = useState(null);
  const [editNom, setEditNom] = useState('');
  const [editEmoji, setEditEmoji] = useState('');
  const [saving, setSaving] = useState(false);

  async function charger() {
    setChargement(true);
    try {
      const data = await api.get(`/evenements/${evenementId}/sides`);
      setSides(data);
    } catch (e) { console.error(e); }
    finally { setChargement(false); }
  }

  useEffect(() => { charger(); }, [evenementId]);

  async function ajouter() {
    if (!formNom.trim()) return;
    setSaving(true);
    try {
      await api.post(`/evenements/${evenementId}/sides`, { nom: formNom.trim(), emoji: formEmoji });
      setFormNom(''); setFormEmoji('🥗');
      charger();
    } catch (e) { alert(e?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  }

  async function sauvegarderEdit(side) {
    try {
      await api.put(`/evenements/sides/${side.id}`, { nom: editNom, emoji: editEmoji });
      setEditId(null);
      charger();
    } catch (e) { alert(e?.response?.data?.error || 'Erreur'); }
  }

  async function toggleSide(side) {
    try {
      await api.patch(`/evenements/sides/${side.id}/toggle`);
      charger();
    } catch (e) { console.error(e); }
  }

  async function supprimerSide(side) {
    if (!window.confirm(`Supprimer "${side.nom}" ?`)) return;
    try {
      await api.delete(`/evenements/sides/${side.id}`);
      charger();
    } catch (e) { alert(e?.response?.data?.error || 'Erreur'); }
  }

  return (
    <div className="onglet-contenu">
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Accompagnements (Sides)</h3>
        <p className="muted" style={{ marginBottom: 16 }}>Ces sides s'affichent sur le formulaire public et s'appliquent à tous les plateaux.</p>

        {chargement ? <div className="muted">Chargement…</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sides.map(side => (
              <div key={side.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--card)', borderRadius: 8, opacity: side.actif ? 1 : 0.5 }}>
                {editId === side.id ? (
                  <>
                    <input type="text" value={editEmoji} onChange={e => setEditEmoji(e.target.value)} style={{ width: 50, textAlign: 'center' }} />
                    <input type="text" value={editNom} onChange={e => setEditNom(e.target.value)} style={{ flex: 1 }} />
                    <button className="btn btn-primary btn-sm" onClick={() => sauvegarderEdit(side)}>✓</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>✕</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 20, minWidth: 28 }}>{side.emoji}</span>
                    <span style={{ flex: 1 }}>{side.nom}</span>
                    <span className={side.actif ? 'success' : 'muted'} style={{ fontSize: 12 }}>
                      {side.actif ? 'Actif' : 'Inactif'}
                    </span>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(side.id); setEditNom(side.nom); setEditEmoji(side.emoji); }} title="Modifier">✏️</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => toggleSide(side)} title="Toggle actif">⏸</button>
                    <button className="btn btn-danger btn-sm" onClick={() => supprimerSide(side)} title="Supprimer">✕</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <input type="text" placeholder="Emoji" value={formEmoji} onChange={e => setFormEmoji(e.target.value)} style={{ width: 60, textAlign: 'center' }} />
          <input
            type="text"
            placeholder="Nouveau side (ex: Salade César)"
            value={formNom}
            onChange={e => setFormNom(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && ajouter()}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" onClick={ajouter} disabled={saving || !formNom.trim()}>+ Ajouter</button>
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Historique ─────────────────────────
function OngletHistorique({ evenementId }) {
  const [historique, setHistorique] = useState([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get(`/evenements/${evenementId}/historique`)
      .then(data => setHistorique(data))
      .catch(e => console.error(e))
      .finally(() => setChargement(false));
  }, [evenementId]);

  if (chargement) return <div className="onglet-contenu"><div className="muted">Chargement…</div></div>;

  return (
    <div className="onglet-contenu">
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="apercu-section-titre">Historique des modifications</h3>
        {historique.length === 0 ? (
          <div className="muted">Aucune modification enregistrée.</div>
        ) : (
          <table className="esc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilisateur</th>
                <th>Champ</th>
                <th>Avant</th>
                <th>Après</th>
              </tr>
            </thead>
            <tbody>
              {historique.map(h => (
                <tr key={h.id}>
                  <td className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{h.modifie_le}</td>
                  <td><span className="badge badge-muted">{h.modifie_par?.toUpperCase()}</span></td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{h.champ_modifie}</td>
                  <td className="muted" style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.ancienne_valeur || '—'}</td>
                  <td style={{ fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.nouvelle_valeur || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Page détail événement ────────────────────

const ONGLETS_BASE = [
  { key: 'apercu',      label: 'Aperçu' },
  { key: 'commandes',   label: 'Commandes' },
  { key: 'plateaux',    label: 'Plateaux' },
  { key: 'slots',       label: 'Slots' },
  { key: 'extras',      label: 'Extras' },
  { key: 'sides',       label: 'Sides' },
  { key: 'par_slot',    label: 'Par slot' },
  { key: 'production',  label: 'Production' },
  { key: 'finances',    label: 'Finances' },
  { key: 'parametres',  label: 'Paramètres' },
  { key: 'historique',  label: 'Historique' },
];

export default function EvenementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [slots, setSlots] = useState([]);
  const [plateaux, setPlateaux] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('apercu');
  const [showModifier, setShowModifier] = useState(false);
  const [showMessage, setShowMessage]   = useState(false);
  const [actionChargement, setActionChargement] = useState(false);
  const [msgAction, setMsgAction] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const evData = await api.get(`/evenements/${id}`);
      setEv(evData);
      // Slots et plateaux sont inclus dans evData — chargement séparé optionnel
      const slotsData = await api.get(`/evenements/${id}/slots`).catch(() => null);
      if (slotsData?.slots) setSlots(slotsData.slots);
      // Utiliser les plateaux déjà inclus dans evData si disponibles
      if (Array.isArray(evData.plateaux)) setPlateaux(evData.plateaux);
    } catch (err) {
      console.error('[EvenementDetail] Erreur chargement:', err?.response?.status, err?.response?.data || err?.message);
      const msg = err?.response?.data?.error || err?.message || 'Erreur inconnue';
      setErreur(`Impossible de charger l'événement : ${msg}`);
    } finally {
      setChargement(false);
    }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  async function handleDupliquer() {
    if (!window.confirm(`Dupliquer "${ev.nom}" en brouillon ?`)) return;
    setActionChargement(true);
    try {
      const data = await api.post(`/evenements/${id}/dupliquer`);
      setMsgAction(`Événement dupliqué ! Nouvel ID : ${data.nouveau_id}`);
      setTimeout(() => { navigate(`/evenements/${data.nouveau_id}`); }, 2000);
    } catch (err) {
      setMsgAction(err?.response?.data?.error || 'Erreur lors de la duplication.');
    } finally {
      setActionChargement(false);
    }
  }

  async function handleAction(action) {
    if (action === 'modifier') {
      setShowModifier(true);
      return;
    }
    setActionChargement(true);
    setMsgAction('');
    try {
      await api.put(`/evenements/${id}/statut`, { statut: action });
      await charger();
      if (action === 'publie') setMsgAction('Événement publié avec succès.');
      else if (action === 'termine') setMsgAction('Événement marqué comme terminé.');
      setTimeout(() => setMsgAction(''), 4000);
    } catch (err) {
      setMsgAction(err?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setActionChargement(false);
    }
  }

  if (chargement) {
    return (
      <div className="evenement-detail-page">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-7) 0' }}>
          {[280, 120, 400].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: i === 2 ? 48 : 20, width: w, borderRadius: 'var(--radius-sm)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (erreur || !ev) {
    return (
      <div className="evenement-detail-page">
        <button className="retour-btn" onClick={() => navigate('/evenements')}>
          <Icon name="retour" size={14} /> Événements
        </button>
        <div className="error-inline" style={{ marginTop: 'var(--space-5)' }}>
          <Icon name="alerte" size={16} />
          {erreur || 'Événement introuvable.'}
        </div>
      </div>
    );
  }

  const statutEv = (STATUTS_EV[ev.statut] || { label: ev.statut, cls: 'badge-muted' });

  return (
    <div className="evenement-detail-page">
      {/* En-tête */}
      <div className="detail-header">
        <button className="retour-btn" onClick={() => navigate('/evenements')}>
          <Icon name="retour" size={14} /> Événements
        </button>
        <div className="detail-header-main">
          <div>
            <div className="detail-meta-ligne">
              <span className={`badge ${statutEv.cls}`}>{statutEv.label}</span>
              <span className="muted" style={{ fontSize: 13 }}>{fmtDate(ev.date_evenement)}</span>
            </div>
            <h1 className="detail-titre">{ev.nom}</h1>
            <div className="detail-lieu">
              <Icon name="transport" size={14} />
              {ev.lieu}{ev.ville ? ` — ${ev.ville}` : ''}
            </div>
          </div>
          <div className="detail-header-actions">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                const token = localStorage.getItem('escamel_token');
                const query = token
                  ? `?preview=1&preview_token=${encodeURIComponent(token)}`
                  : '?preview=1';
                window.open(`${PUBLIC_FORM_URL}/evenement/${ev.slug}${query}`, '_blank');
              }}
              title="Prévisualiser le formulaire public"
            >
              <Icon name="voir" size={14} /> Prévisualiser
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleDupliquer}
              disabled={actionChargement}
              title="Dupliquer cet événement"
            >
              <Icon name="liste" size={14} /> Dupliquer
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/evenements/${id}/jour-j`)}
              title="App Jour J"
            >
              <Icon name="fumage" size={14} /> Jour J
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowMessage(true)}
              title="Envoyer un message aux clients"
            >
              <Icon name="email" size={14} /> Message
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => exporterPDF(id)}
              title="Exporter la feuille de service PDF"
            >
              <Icon name="document" size={14} /> PDF
            </button>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="detail-onglets">
        {[
          ...ONGLETS_BASE,
          ...(ev.type === 'corporatif' ? [{ key: 'corpo', label: 'Corporatif' }] : []),
        ].map(o => (
          <button
            key={o.key}
            className={`onglet-btn ${onglet === o.key ? 'onglet-actif' : ''}`}
            onClick={() => setOnglet(o.key)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Contenu par onglet */}
      {onglet === 'apercu' && (
        <OngletApercu
          ev={ev}
          slots={slots}
          plateaux={plateaux}
          onAction={handleAction}
          chargement={actionChargement}
          msgAction={msgAction}
        />
      )}
      {onglet === 'commandes' && <OngletCommandes evenementId={id} />}
      {onglet === 'plateaux' && (
        <OngletPlateaux
          evenementId={id}
          plateaux={plateaux}
          onUpdatePlateaux={setPlateaux}
        />
      )}
      {onglet === 'slots' && <OngletSlots evenementId={id} ev={ev} />}
      {onglet === 'sides' && <OngletSides evenementId={id} />}
      {onglet === 'par_slot' && <OngletParSlot evenementId={id} capaciteSlot={ev.capacite_slot || 40} />}
      {onglet === 'production' && <OngletProduction evenementId={id} />}
      {onglet === 'extras'    && <OngletExtras    evenementId={id} />}
      {onglet === 'finances' && <OngletFinances evenementId={id} />}
      {onglet === 'parametres' && (
        <OngletParametres
          ev={ev}
          plateaux={plateaux}
          evenementId={id}
          onUpdateEv={(data) => setEv(prev => ({ ...prev, ...data }))}
          onUpdatePlateaux={setPlateaux}
          onUpdateProd={(data) => setEv(prev => ({ ...prev, production: data }))}
        />
      )}
      {onglet === 'corpo' && ev.type === 'corporatif' && (
        <OngletClientCorpo
          ev={ev}
          evenementId={id}
          onUpdateEv={(data) => setEv(prev => ({ ...prev, ...data }))}
        />
      )}

      {onglet === 'historique' && <OngletHistorique evenementId={id} />}

      {showModifier && (
        <ModalModifierEvenement
          ev={ev}
          onClose={() => setShowModifier(false)}
          onSauvegarde={(updated) => {
            setEv(updated);
            setShowModifier(false);
          }}
        />
      )}

      {showMessage && (
        <ModalMessageClients
          evenementId={id}
          nbConfirmees={ev.stats?.nb_commandes_confirmees ?? ev.nb_commandes}
          slots={slots}
          onClose={() => setShowMessage(false)}
        />
      )}
    </div>
  );
}
